import React, {
  Fragment, useContext, useCallback, useRef, useEffect
} from 'react';
import {
  bool, shape, string, number
} from 'prop-types';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { useDomPath, useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { Row, Col } from '@thd-olt-component-react/grid';
import { useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import { Fulfillment } from '@thd-olt-component-react/fulfillment';
import { ProductAddOns } from '@thd-olt-component-react/product-add-ons';
import { SubscribeAndSave } from '@thd-olt-component-react/subscribe-and-save';
import {
  extend,
  useDataModel
} from '@thd-nucleus/data-sources';
import { InstantCheckout } from '@thd-olt-component-react/instant-checkout';

import classNames from 'classnames';
import { BuyboxError } from './subcomponents/BuyboxError';
import { BuyboxUnavailable } from './subcomponents/BuyboxUnavailable';
import { BuyboxCalculator } from './subcomponents/BuyboxCalculator';
import { BuyboxLite } from './subcomponents/BuyboxLite';
import { BuyboxActions } from './subcomponents/BuyboxActions';
import { BuyboxLoader } from './subcomponents/BuyboxLoader';
import { useBuyBox } from './hooks/useBuyBox';
import * as helper from '../BuyboxHelper';
import * as calculatorHelper from './BuyboxCalcHelper';
import { track } from '../track';
import { dataModel } from './dataModel';
import './buybox.scss';
import { isMobile } from '../util';

/**
 * Buybox container
 */
const Buybox = (props) => {
  const {
    channel,
    isServer
  } = useContext(ExperienceContext);

  const buyboxTimerRef = useRef(new Date());

  const {
    stash,
    hideQuantity,
    disableATC: buyboxDisableATC,
    lite,
    sticky,
    internally,
    lmUseContextForProps,
    hideInstantCheckout,
    hidePaypalCheckout,
    hideProReferral,
    showCards
  } = props;

  let { itemId } = props;
  const {
    bodfsMinimumThreshold,
    bopisMinimumThreshold,
    bopisMinimumThresholdStores,
    bopisHolidayTiming,
    covidAlert,
    isTwoTileEnabled
  } = useLayoutGroupProps({ props });

  const getItemIdFromConfigurator = (items) => {
    if (items) {
      return items.find((item) => item.isAnchor).itemId;
    }
    return '';
  };

  const { storeId, storeZip } = useStore();
  const [buyboxState, setBuyboxState] = useBuyBox({ storeZip });
  const { items } = useLifeCycleEventBus('configurator.configurator_available_items');
  if (items) {
    itemId = getItemIdFromConfigurator(items) || itemId;
  }
  const atcStash = helper.useATCLocationDDOStash({ stash, itemId });

  const productOptions = {
    skip: storeId === '8119',
    ssr: false,
    variables: {
      itemId
    }
  };

  const { data, loading, error } = useDataModel('clientOnlyProduct', productOptions);

  useEffect(() => {
    if (data?.product) {
      if (typeof LIFE_CYCLE_EVENT_BUS !== 'undefined') {
        const start = buyboxTimerRef.current.getTime();
        const end = new Date().getTime();
        LIFE_CYCLE_EVENT_BUS.trigger('buybox.loaded', { start, end });
      }
    }
  }, [data?.product]);

  const {
    fulfillment = {},
    info = {},
    availabilityType = {},
    pricing = {},
    installServices = {},
    specificationGroup = [],
    identifiers = {},
  } = data?.product || {};
  const parentId = identifiers?.parentId || '';

  const onQuantityChange = useCallback(({ quantity }) => {
    const qty = parseInt(quantity, 10);
    if (!Number.isNaN(qty) && qty > 0) {
      setBuyboxState((prevState) => {
        return {
          ...prevState,
          quantity: qty,
          showTooltip: true
        };
      });
    }
  }, [buyboxState]);

  const onFulfillmentChange = ({
    currentFulfillment,
    method,
    isShowATC,
    disableATC,
    isOutOfStockOnline,
    quantityLimitExceed,
    availableFulfillments
  }) => {
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        fulfillmentMethod: method,
        availableFulfillments,
        isShowATC,
        disableATC,
        isOutOfStockOnline,
        currentFulfillment,
        quantityLimitExceed,
        // Reset Subscribe & save on fulfillment change
        productAddOns: {
          ...prevState.productAddOns,
          subscriptions: {
            ...prevState.productAddOns.subscriptions,
            frequency: null
          }
        }
      };
    });
  };

  const onProductAddOnChange = (addOnData, productAddOnData) => {
    const { addOn, flags } = helper.getSelectedAddOnInfo(addOnData, buyboxState.fulfillmentMethod);
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        ...flags,
        productAddOns: {
          ...prevState.productAddOns,
          ...addOn
        }
      };
    });
  };

  const onSubscribeAndSaveChange = ({ frequency, isOptIn }) => {
    // isOptIn will be true if they have selected to subscribe
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        productAddOns: {
          ...prevState.productAddOns,
          subscriptions: {
            ...prevState.productAddOns.subscriptions,
            frequency: isOptIn ? frequency : null,
          }
        }
      };
    });
  };

  const onChangeCalculator = ({ quantity, coverage, error: errorCalc }) => {
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        quantity,
        coverage,
        showTooltip: !errorCalc
      };
    });
  };

  const updateAdditionalQuantity = ({ addAdditional, size }) => {
    const { coverage, quantity } = buyboxState;
    let updatedQty = quantity;
    if (coverage) {
      const needed = addAdditional ? +coverage + (+coverage * 0.10) : coverage;
      updatedQty = Math.ceil(needed / size);
    } else if (addAdditional) {
      updatedQty = Math.ceil(+updatedQty + (+updatedQty * 0.10));
    }
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        quantity: updatedQty || quantity,
        addAdditional
      };
    });
  };
  // ######## End of Calculator methods ##########
  const onDeliveryOptionsZipCodeChange = useCallback(({ zipCode }) => {
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        zipCode
      };
    });
  }, []);

  // call back on add to cart click
  const onATCClick = useCallback(() => {
    if (window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('@@ADD-TO-CART/LOCATION', { ...atcStash, parentId });
    }
  }, [itemId, parentId]);

  const unavailable = buyboxDisableATC; // From prop
  const { hasSubscription, minimumOrderQuantity } = info || {};
  const {
    isOutOfStockOnline,
    isShowATC,
    quantity,
    quantityLimitExceed,
    showTooltip,
    addAdditional,
    zipCode,
    fulfillmentMethod,
    paypalEligible,
    productAddOns,
    availableFulfillments,
    assembleItemAtStore
  } = buyboxState;
  const isATCDisabled = unavailable || buyboxState?.disableATC;

  const { dualPath = false, virtualAssistance = false } = productAddOns;

  const orderQuantity = minimumOrderQuantity > quantity ? minimumOrderQuantity : quantity;

  const [pathForAdvisoryMessage, refForAdvisoryMessage] = useDomPath();

  if (error && !data) {
    return (
      <BuyboxError />
    );
  }

  if (loading || isServer) {
    return (
      <BuyboxLoader
        showCards
        channel={channel}
        lite={lite}
      />
    );
  }

  if (!data?.product) {
    return null;
  }

  if (unavailable) {
    return (
      <BuyboxUnavailable
        hideQuantity={hideQuantity}
        itemId={itemId}
        quantity={orderQuantity}
        quantityLimitExceed={quantityLimitExceed}
        onQuantityChange={onQuantityChange}
        onFulfillmentChange={onFulfillmentChange}
        onDeliveryOptionsZipCodeChange={onDeliveryOptionsZipCodeChange}
      />
    );
  }
  if (lite) {
    return (
      <BuyboxLite
        itemId={itemId}
        configurableItems={items}
        unavailable={unavailable}
        covidAlert={covidAlert}
        storeId={storeId}
        isOutOfStockOnline={isOutOfStockOnline}
        isShowATC={isShowATC}
        storeZip={storeZip}
        isATCDisabled={isATCDisabled}
        channel={channel}
        zipCode={zipCode}
        fulfillmentMethod={fulfillmentMethod}
        fulfillment={fulfillment}
        paypalEligible={paypalEligible}
        availabilityType={availabilityType}
        onFulfillmentChange={onFulfillmentChange}
        onDeliveryOptionsZipCodeChange={onDeliveryOptionsZipCodeChange}
        onATCClick={onATCClick}
        internally={internally}
        bopisHolidayTiming={bopisHolidayTiming}
        lmUseContextForProps={lmUseContextForProps}
        hideInstantCheckout={hideInstantCheckout}
        hidePaypalCheckout={hidePaypalCheckout}
      />
    );
  }
  const inventoryWarningMessage = !isOutOfStockOnline
    && isShowATC
    && !!minimumOrderQuantity
    && `We can only ship quantities of ${minimumOrderQuantity} or more`;

  const wrapperClasses = classNames('buybox-wrapper', { 'buybox-wrapper--card-background': showCards });
  const cardClasses = classNames({ 'buybox-top__wrapper': !showCards, buybox__card: showCards });
  const WrapperTag = isMobile(channel) ? Fragment : Row;

  return (
    <WrapperTag>
      <Col fallback="12" nopadding={showCards} className={wrapperClasses}>
        <div className={cardClasses}>
          {!unavailable && !virtualAssistance && (
          /* eslint-disable-next-line no-sequences */
            <div className={showCards ? '' : 'buybox__fulfillment'}>
              <Fulfillment
                itemId={itemId}
                covidAlert={covidAlert}
                quantity={orderQuantity}
                onChange={onFulfillmentChange}
                onDeliveryZipCodeChange={onDeliveryOptionsZipCodeChange}
                dualPath={dualPath}
                bodfsMinimumThreshold={bodfsMinimumThreshold}
                bopisMinimumThreshold={bopisMinimumThreshold}
                bopisMinimumThresholdStores={bopisMinimumThresholdStores}
                internally={internally}
                bopisHolidayTiming={bopisHolidayTiming}
                isTwoTileEnabled={isTwoTileEnabled}
              />
            </div>
          )}
        </div>
        {!isOutOfStockOnline && calculatorHelper.shouldDisplayCalculator({ pricing, info }) && !dualPath && (
          <div className={cardClasses}>
            <BuyboxCalculator
              showTooltip={showTooltip}
              addAdditional={addAdditional}
              channel={channel}
              quantity={orderQuantity}
              pricing={pricing}
              info={info}
              fulfillment={fulfillment}
              installServices={installServices}
              specificationGroup={specificationGroup}
              onChangeCalculator={onChangeCalculator}
              updateAdditionalQuantity={updateAdditionalQuantity}
            />
          </div>
        )}
        {!isOutOfStockOnline && isShowATC && (
          <>
            <ProductAddOns
              itemId={itemId}
              fulfillment={buyboxState.fulfillmentMethod}
              onChange={onProductAddOnChange}
              availableFulfillments={availableFulfillments}
              hideProReferral={hideProReferral}
              showCards={showCards}
            />

            {hasSubscription && (
              <div className={cardClasses}>
                <SubscribeAndSave
                  itemId={itemId}
                  quantity={orderQuantity}
                  fulfillment={buyboxState.fulfillmentMethod}
                  onChange={onSubscribeAndSaveChange}
                />
              </div>
            )}
          </>
        )}
        <div className={cardClasses}>
          {inventoryWarningMessage && (
            <>
              {pathForAdvisoryMessage.length && track('advisory-message', {
                message: inventoryWarningMessage,
                path: pathForAdvisoryMessage
              })}
              <div className="alert-inline alert-inline--warning inventory--warning" ref={refForAdvisoryMessage}>
                <span>{inventoryWarningMessage}</span>
              </div>
            </>
          )}
          {!isOutOfStockOnline && isShowATC && !dualPath && !virtualAssistance && (
            <BuyboxActions
              alterBrowserHistory
              disableATC={isATCDisabled}
              fulfillment={fulfillment}
              fulfillmentMethod={fulfillmentMethod}
              hideQuantity={hideQuantity}
              isAssembleItemAtStore={assembleItemAtStore}
              itemId={itemId}
              configurableItems={items}
              minimumOrderQuantity={minimumOrderQuantity}
              onATCClick={onATCClick}
              onQuantityChange={onQuantityChange}
              paypalEligible={paypalEligible}
              productAddOns={productAddOns}
              quantity={orderQuantity}
              quantityLimitExceed={quantityLimitExceed}
              sticky={sticky}
              storeId={storeId}
              storeZip={storeZip}
              zipCode={zipCode}
              showCards={showCards}
            />
          )}
        </div>
      </Col>
    </WrapperTag>
  );
};

Buybox.displayName = 'Buybox';
Buybox.propTypes = {
  /**
   * The number of clicks you are allowed before seeing a warning.
   */
  itemId: string,
  /** will hide the buybox quantity box */
  hideQuantity: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  covidAlert: bool,
  /** Pass ddo values to override the default */
  disableATC: bool,
  lite: bool,
  stash: shape({
    pageType: string,
    sharedSection: string,
    section: string,
    component: string
  }),
  sticky: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bodfsMinimumThreshold: number,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bopisMinimumThreshold: number,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bopisMinimumThresholdStores: string,
  /* eslint-disable-next-line react/no-unused-prop-types */
  isTwoTileEnabled: bool,
  internally: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bopisHolidayTiming: number,
  lmUseContextForProps: bool,
  hideInstantCheckout: bool,
  hidePaypalCheckout: bool,
  hideProReferral: bool,
  showCards: bool
};

Buybox.defaultProps = {
  itemId: null,
  lite: false,
  hideQuantity: false,
  disableATC: false,
  stash: {},
  covidAlert: false,
  sticky: false,
  bodfsMinimumThreshold: null,
  bopisMinimumThreshold: null,
  bopisMinimumThresholdStores: null,
  isTwoTileEnabled: false,
  internally: false,
  bopisHolidayTiming: null,
  lmUseContextForProps: false,
  hideInstantCheckout: false,
  hidePaypalCheckout: false,
  hideProReferral: false,
  showCards: false
};

Buybox.dataModel = extend(
  dataModel,
  Fulfillment,
  ProductAddOns,
  SubscribeAndSave,
  InstantCheckout
);

export default Buybox;
